// @flow
import invariant from 'invariant';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { Location } from 'react-router';
import { trackSignUp } from 'utils/analytics';
import configureTrackJs from 'utils/configureTrackJs';
import { postJson } from 'utils/http';
import { resetRecaptcha } from 'utils/recaptcha';
import getThirdPartyLoginUrl from 'utils/getThirdPartyLoginUrl';
import EmailSignupForm from 'components/EmailSignupForm';
import type { CurrentUser } from 'api/currentUser';
import { updateCurrentUser } from '../redux/main/actions/currentUser';
import { showModal } from '../redux/main/actions/modal';

type OwnProps = {
  isModal: boolean,
  redirectTo?: string,
  signupEmailParams?: Object,
  savedListing: ?Object,
  subscribedSubsite: ?string,
  location: Location,
  onSignup?: Function,
  onLogin?: CurrentUser => void,
  loginOverrideFn?: Function,
  compactView?: boolean,
  hideLoginBlock?: boolean
};

type Props = {
  ...$Exact<OwnProps>,
  updateCurrentUser: Function,
  showModal: typeof showModal
};

class EmailSignupFormContainer extends React.PureComponent<Props> {
  static defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    signupEmailParams: {}
  };

  get initialValues() {
    return {
      email: '',
      recaptchaKey: 0,
      gRecaptchaResponse: undefined
    };
  }

  showLoginModal = (email?: string, initialWarning?: string) => {
    this.props.showModal('LOGIN', {
      redirectTo: this.props.redirectTo,
      savedListing: this.props.savedListing,
      subscribedSubsite: this.props.subscribedSubsite,
      email,
      onLogin: this.props.onLogin,
      initialWarning
    });
  };

  getLoginUrl = (email?: string) => {
    const { redirectTo, savedListing, subscribedSubsite } = this.props;
    return {
      pathname: '/login',
      state: {
        redirectTo,
        savedListing,
        subscribedSubsite,
        email
      }
    };
  };

  getThirdPartyLoginUrl = (provider: 'facebook' | 'google') => {
    const {
      location,
      redirectTo,
      savedListing,
      subscribedSubsite
    } = this.props;
    return getThirdPartyLoginUrl({
      provider,
      redirectTo: redirectTo || `${location.pathname}${location.search}`,
      savedListing,
      subscribedSubsite
    });
  };

  onSubmit = ({ values, handleError }) => {
    const { email, recaptchaKey, gRecaptchaResponse } = values;

    postJson('/data/website/signup-email', {
      email,
      gRecaptchaResponse,
      redirectTo: this.props.redirectTo,
      ...this.props.signupEmailParams
    })
      .then(({ user }) => {
        invariant(user.id, 'Missing user id');
        trackSignUp(user, 'Email');
        configureTrackJs(user.id);
        this.props.updateCurrentUser(user);
        if (this.props.onSignup) {
          this.props.onSignup();
        }
      })
      .catch(error => {
        const errorArray = error.responseJson && error.responseJson.errors;
        const emailInUseError =
          errorArray &&
          errorArray.find(e => e.name === 'email' && e.type === 'alreadyInUse');
        if (emailInUseError) {
          this.showLoginModal(
            email,
            'An account with this email exists. Did you mean to login?'
          );
        } else {
          resetRecaptcha();
          handleError(error, {
            recaptchaKey: recaptchaKey + 1,
            gRecaptchaResponse: undefined
          });
        }
      });
  };

  render() {
    return (
      <EmailSignupForm
        isModal={this.props.isModal}
        initialValues={this.initialValues}
        showLoginModal={this.showLoginModal}
        getLoginUrl={this.getLoginUrl}
        getThirdPartyLoginUrl={this.getThirdPartyLoginUrl}
        loginOverrideFn={this.props.loginOverrideFn}
        onSubmit={this.onSubmit}
        compactView={this.props.compactView}
        hideLoginBlock={this.props.hideLoginBlock}
      />
    );
  }
}

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    // $FlowFixMe: react-redux complains about this type when wrapped in withRouter
    null,
    // $FlowFixMe: react-redux complains about this type when wrapped in withRouter
    { showModal, updateCurrentUser }
  )(EmailSignupFormContainer)
);
